import { useRef, useEffect, useState } from 'react';

export const useInfiniteScroll = (speed = 0.5) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [isHovered, setIsHovered] = useState(false);
    const animationRef = useRef<number>();
    const lastTimestampRef = useRef<number>(0);
    const accumulatedDeltaRef = useRef<number>(0);

    useEffect(() => {
        const scrollContainer = scrollRef.current;
        if (!scrollContainer) return;

        // Add hover handlers
        const handleMouseEnter = () => setIsHovered(true);
        const handleMouseLeave = () => setIsHovered(false);

        scrollContainer.addEventListener('mouseenter', handleMouseEnter);
        scrollContainer.addEventListener('mouseleave', handleMouseLeave);

        const animate = (timestamp: number) => {
            if (!lastTimestampRef.current) lastTimestampRef.current = timestamp;
            const delta = timestamp - lastTimestampRef.current;
            lastTimestampRef.current = timestamp;

            if (!isHovered && scrollContainer) {
                // Accumulate delta time and only scroll when enough time has passed
                accumulatedDeltaRef.current += delta;
                
                // Scroll approximately every 16ms (60fps)
                if (accumulatedDeltaRef.current >= 16) {
                    scrollContainer.scrollLeft += speed * (accumulatedDeltaRef.current / 16);
                    accumulatedDeltaRef.current = 0;
                }

                // Reset scroll position smoothly when reaching the end
                const { scrollLeft, scrollWidth, clientWidth } = scrollContainer;
                const maxScroll = scrollWidth - clientWidth;
                
                if (scrollLeft >= maxScroll) {
                    scrollContainer.scrollLeft = 0;
                }
            }

            animationRef.current = requestAnimationFrame(animate);
        };

        animationRef.current = requestAnimationFrame(animate);

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
            scrollContainer.removeEventListener('mouseenter', handleMouseEnter);
            scrollContainer.removeEventListener('mouseleave', handleMouseLeave);
        };
    }, [speed, isHovered]);

    return scrollRef;
};